// ===========================================
// NEEDS YOU — the morning worklist above the board
// ===========================================
// One list that answers "what do I do first": overdue next steps, invoices
// newly crossing 30/60 days, fresh deductions, accounts past their reorder
// rhythm. Every row is a jump into that account's panel. Computed entirely
// from queries the page already holds — no extra fetches, no spinners.

import { useState } from 'react';
import clsx from 'clsx';
import { CalendarClock, Receipt, Scissors, RotateCcw, ListChecks, ChevronDown, ChevronRight } from 'lucide-react';
import type { NeedsYouItem } from './insights';

const KIND_META: Record<NeedsYouItem['kind'], { icon: typeof Receipt; tone: string; chip: string }> = {
  invoice_60: { icon: Receipt, tone: 'text-red-600', chip: 'bg-red-50 text-red-700' },
  next_step: { icon: CalendarClock, tone: 'text-red-600', chip: 'bg-red-50 text-red-700' },
  invoice_30: { icon: Receipt, tone: 'text-amber-700', chip: 'bg-amber-50 text-amber-700' },
  deduction: { icon: Scissors, tone: 'text-amber-700', chip: 'bg-amber-50 text-amber-700' },
  reorder: { icon: RotateCcw, tone: 'text-slate-500', chip: 'bg-slate-100 text-slate-600' },
};

const KIND_CHIP_LABEL: Record<NeedsYouItem['kind'], string> = {
  next_step: 'overdue step', invoice_60: '60d+', invoice_30: '30d+', deduction: 'deduction', reorder: 'reorder',
};

const SHOW_FIRST = 6;

export default function NeedsYouQueue({ items, onOpen }: { items: NeedsYouItem[]; onOpen: (accountId: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, SHOW_FIRST);

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="font-semibold text-slate-900 flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-primary-600" /> Needs you
          {items.length > 0 && <span className="text-xs font-medium text-slate-400">{items.length} item{items.length === 1 ? '' : 's'}</span>}
        </p>
        <p className="text-[11px] text-slate-400">From your notes, orders, invoices and deductions — most urgent first.</p>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-slate-400">
          Nothing needs you right now. Overdue next steps, invoices crossing 30 or 60 days,
          fresh deductions to review and accounts past their reorder rhythm would appear here.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {shown.map((it) => {
            const meta = KIND_META[it.kind];
            const Icon = meta.icon;
            return (
              <button key={it.key} onClick={() => onOpen(it.accountId)}
                className="w-full text-left flex items-start gap-3 py-2 px-1 -mx-1 rounded-lg hover:bg-slate-50 transition-colors group">
                <Icon className={clsx('w-4 h-4 mt-0.5 shrink-0', meta.tone)} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 truncate">
                    <span className="font-medium group-hover:text-primary-600">{it.accountName}</span>
                    <span className="text-slate-500"> — {it.what}</span>
                  </p>
                  <p className="text-xs text-slate-400 truncate">{it.why}</p>
                </div>
                <span className={clsx('shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium mt-0.5', meta.chip)}>
                  {KIND_CHIP_LABEL[it.kind]}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {items.length > SHOW_FIRST && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary-600 font-medium flex items-center gap-1">
          {expanded ? <><ChevronDown className="w-3.5 h-3.5" /> Show fewer</> : <><ChevronRight className="w-3.5 h-3.5" /> Show all {items.length}</>}
        </button>
      )}
    </div>
  );
}
